"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObservableSet = void 0;
const decorators_1 = require("../decorators");
const observable_collection_1 = require("./observable-collection");
/**
 * An observable set.
 *
 * Observers will be notified whenever the content of the set is changed.
 */
let ObservableSet = class ObservableSet extends observable_collection_1.ObservableCollection {
    constructor(iterable) {
        super();
        this[_a] = 'ObservableSet';
        this.set = new Set(iterable);
    }
    /**
     * Add a single element to the set.
     *
     * If the element was not previously present in the set, observers will be
     * notified.
     *
     * @param element the element to add to the set.
     * @returns `true` if the element was added, or `false` if it was already
     * 	present.
     */
    add(element) {
        const preSize = this.set.size;
        this.set.add(element);
        if (preSize !== this.set.size) {
            this._changed();
            return true;
        }
        return false;
    }
    /**
     * Add multiple elements to the set.
     *
     * If any of the passed elements were not present in the set,
     * observers will be notified.
     *
     * @param elements the elements to add to the set.
     * @returns `true` if any of the elements were added, or `false` if all the elements
     * 	were already present.
     */
    addAll(elements) {
        const preSize = this.set.size;
        for (const element of elements) {
            this.set.add(element);
        }
        if (preSize !== this.set.size) {
            this._changed();
            return true;
        }
        return false;
    }
    /**
     * Remove a single element from the set.
     *
     * If the element was previously present in the set, observers will be notified.
     *
     * @param element the element to remove.
     * @returns `true` if the element was removed, or `false` if the element was not
     * 	present in the set.
     */
    delete(element) {
        if (this.set.delete(element)) {
            this._changed();
            return true;
        }
        return false;
    }
    /**
     * Remove multiple elements from the set.
     *
     * If any elements were previously present in the set, observers will be
     * notified.
     *
     * @param elements the elements to remove.
     * @returns `true` if any of the elements were removed, or `false` if none of
     * 	the elements was present in the set.
     */
    deleteAll(elements) {
        let anyRemoved = false;
        for (const element of elements) {
            anyRemoved = this.set.delete(element) || anyRemoved;
        }
        if (anyRemoved) {
            this._changed();
        }
        return anyRemoved;
    }
    /**
     * Remove all elements from the set.
     *
     * If the set previously had any elements, observers will be notified.
     *
     * @returns `true` if the set was cleared, or `false` if the set was already
     * 	empty.
     */
    clear() {
        if (this.set.size === 0) {
            return false;
        }
        this.set.clear();
        this._changed();
        return true;
    }
    /**
     * Replace all elements in the set.
     */
    replace(elements) {
        this.clear();
        for (const element of elements) {
            this.set.add(element);
        }
        this._changed();
    }
    /**
     * Invokes `callback` for each element in the set, in insertion order.
     */
    forEach(callback) {
        this.set.forEach(value => callback(value, this));
    }
    filter(predicate) {
        const filteredElements = [];
        for (const element of this.set) {
            if (predicate(element, this)) {
                filteredElements.push(element);
            }
        }
        return filteredElements;
    }
    /**
     * Create an array of this set's elements, mapped by `callback`.
     */
    map(callback) {
        const mappedElements = [];
        for (const element of this.set) {
            const mappedValue = callback(element, mappedElements.length, this);
            mappedElements.push(mappedValue);
        }
        return mappedElements;
    }
    /**
     * Test if `element` is present in the set.
     */
    has(element) {
        return this.set.has(element);
    }
    /**
     * Get the number of elements in the set.
     */
    get size() {
        return this.set.size;
    }
    /**
     * Create an array containing the elements of this set, in insertion order.
     */
    toArray() {
        return [...this.set];
    }
    [Symbol.iterator]() {
        return this.set[Symbol.iterator]();
    }
};
_a = Symbol.toStringTag;
ObservableSet = __decorate([
    (0, decorators_1.subject)()
], ObservableSet);
exports.ObservableSet = ObservableSet;
//# sourceMappingURL=observable-set.js.map